/**
 * Prefilling via the address: `?guide=<actionId>&<field>=<value>`.
 *
 * Stage 2 of the portal guide still writes nothing (`CONTRACT.md` §7). But the
 * assistant can do more than bring the user to the right area (B2) and point at
 * the right button (B3) — it can also put the create dialog in front of them
 * **open and prefilled**. Nothing is saved until the human presses the save
 * button: the hook sets form state, nothing else.
 *
 * The pattern comes from the existing code and is deliberately not reinvented:
 * `AgentPanel.tsx:651-666` has always opened the order dialog from the address
 * (`?openOrder=true`) and **removes the parameter first**, and
 * `ProcessPanel.tsx:1186-1192` describes in a comment the bug that the area
 * comparison prevents: `SettingsPage` swaps areas via a `useState` that lags one
 * render pass behind, so the area being **left** sees the new address one more
 * time. Without that check, dialogs open in the wrong area and overwrite its
 * parameters.
 *
 * The hook does three things, and only these three:
 *
 * 1. **Bail out** if the address's `panel`/`tab` are not its own.
 * 2. **Fire exactly once per request** (a ref, not state — a state change would
 *    trigger another render pass before the effect has finished). What is
 *    remembered is the **handled address**, not a boolean flag: panels stay
 *    mounted across a pure query change (`SettingsPage` only swaps the area
 *    component for a different `?panel=`, and the guide navigates within
 *    `/settings` with `replace`), so a once-per-mount flag would let the
 *    **second** prefill of the same panel go nowhere — and, worse, leave the
 *    fields in the address (for the invitation: e-mail, first and last name).
 * 3. **Clean up immediately**: `guide` and all allowed fields leave the address
 *    via `setSearchParams(next, { replace: true })`. Without the cleanup the
 *    dialog would reopen after every back click and every reload — and personal
 *    fields (invitation) would stay in the history longer than necessary.
 *
 * **The field list comes from the registry, not from the caller.** The entry's
 * `guideAction.fields` is the only source; anything not listed there is silently
 * dropped. That leaves exactly one place where a field is allowed, and the schema
 * guard (`featureRegistry.schema.test.ts`: blocklist
 * `password`/`token`/`key`/`secret`, mandatory marking `personalFields`) guards it.
 */

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PORTAL_FEATURES } from './featureRegistry';
import {
    GUIDE_ACTION_LIST_SEPARATOR,
    GUIDE_ACTION_VALUE_MAX_LENGTH,
    sanitizeGuideActionValue,
} from './types';
import type { PortalFeature } from './types';

/** Name of the address parameter that triggers the prefill. */
export const GUIDE_ACTION_PARAM = 'guide';

/** The prefill as the opener receives it: field name → already sanitised value. */
export type GuideActionValues = Record<string, string>;

export interface GuideActionScope {
    /**
     * Value of `?panel=` under which this panel runs — **required**.
     *
     * Not optional, even though the hook could live with that: a call without an
     * area would open its dialog even when the address refers to a different area —
     * exactly the bug the area comparison is meant to prevent (`SettingsPage` swaps
     * the area component one render pass late, so the area being left sees the new
     * address one more time). All seven panels pass it anyway; the type keeps it
     * that way instead of leaving it to the next caller.
     */
    panel: string;
    /** Value of `?tab=` if the action belongs to a tab. */
    tab?: string;
    /**
     * Whether the opener can achieve anything yet. Intended for
     * `guideAction.requiresState`: the hook calls the opener directly and bypasses
     * the (disabled) button, but the button's condition depends on lazily loaded
     * data (roles, stored company). As long as `ready` is false, the hook waits.
     *
     * If the condition stays unmet permanently (no roles, no company), the hook
     * never fires: nothing opens, the parameters stay in the address and the
     * panel's existing hint text remains visible. That is the intended order —
     * better a hint than a form the user cannot complete.
     */
    ready?: boolean;
}

/** The registry entry whose `guideAction.id` carries this id. */
export function findFeatureByGuideAction(actionId: string): PortalFeature | undefined {
    return PORTAL_FEATURES.find((feature) => feature.guideAction?.id === actionId);
}

/**
 * A value in the form a form field may accept.
 *
 * Line breaks become spaces: an address can carry them (`%0A`), a single-line
 * input would show them as spaces, a multi-line one would keep them — the same
 * prefill would look different per field type, and a name with a line break
 * would end up in the database that way. After that, the same truncation as on
 * the server side (`sanitizeGuideActionValue`: curly braces removed, 200
 * characters).
 */
export function normalizeGuideActionValue(raw: string): string {
    return sanitizeGuideActionValue(raw.replace(/[\r\n\t\u2028\u2029]+/g, ' ').trim());
}

/** The allowed fields from the address — nothing else gets through. */
export function readGuideActionValues(
    params: URLSearchParams,
    fields: readonly string[],
): GuideActionValues {
    const values: GuideActionValues = {};
    fields.forEach((field) => {
        const raw = params.get(field);
        if (raw === null) return;
        const value = normalizeGuideActionValue(raw);
        if (value === '') return;
        values[field] = value;
    });
    return values;
}

/**
 * Splits a field from `guideAction.listFields` into its entries. The address
 * carries exactly one value per field, so lists arrive as a comma-separated list.
 *
 * **The last entry is dropped if the truncation cut into the middle of it.** The
 * whole field value is truncated (`sanitizeGuideActionValue`, 200 characters) —
 * splitting only happens afterwards, so the last keyword of an overly long list
 * would be a fragment („brandschutz" from „brandschutzordnung"). A fragment is
 * worse as a search term than a missing one: it looks like an intended term but
 * searches for something else. If the value ends on the separator (even with
 * spaces after it, because truncation happens after trimming), the truncation
 * hit exactly the boundary and everything that remains is complete.
 */
export function splitGuideActionList(value: string | undefined): string[] {
    if (value === undefined) return [];
    const entries = value
        .split(GUIDE_ACTION_LIST_SEPARATOR)
        .map((entry) => entry.trim())
        .filter((entry) => entry !== '');
    const truncated =
        value.length >= GUIDE_ACTION_VALUE_MAX_LENGTH &&
        !value.trimEnd().endsWith(GUIDE_ACTION_LIST_SEPARATOR);
    return truncated ? entries.slice(0, -1) : entries;
}

/**
 * Opens a panel's create dialog prefilled when the address asks for it.
 *
 * @param actionId `guideAction.id` of the registry entry — the same id as
 *   `guideId` and as the button's `data-guide-id`.
 * @param opener Sets the dialog state prefilled (e.g.
 *   `setSelectedAlert(new BusinessRule(values))`). Called at most once per request
 *   (address with `?guide=<actionId>`); the function's identity may change on
 *   every render pass.
 * @param scope Area (required) and tab under which this panel runs, plus optional
 *   readiness.
 */
export function useGuideAction(
    actionId: string,
    opener: (values: GuideActionValues) => void,
    scope: GuideActionScope,
): void {
    const [searchParams, setSearchParams] = useSearchParams();
    /**
     * The most recently handled address (the whole query, not just `guide`). A
     * repeated render pass with the same address finds it here and does nothing; a
     * **new** request has a different query and fires again. When the address no
     * longer contains a request for us, the memo is cleared — otherwise an
     * identical address that comes back later could get stuck.
     */
    const handled = useRef<string | null>(null);
    /**
     * The opener as a ref: it closes over the panel state and is a new function on
     * every render pass. If it were in the dependencies, the effect would run again
     * on every render pass — harmless because of `handled`, but it would obscure
     * when it really fires.
     */
    const openerRef = useRef(opener);
    // In the effect, not in the body: the render phase is side-effect free, and a
    // discarded pass (concurrent rendering, `<React.StrictMode>`) would still leave
    // its opener in the ref. No dependency list, so after **every** commit — and
    // because effects run in order, this holds the opener of the most recently
    // committed pass before the trigger effect below calls it.
    useEffect(() => {
        openerRef.current = opener;
    });

    const { panel, tab, ready = true } = scope;

    useEffect(() => {
        if (searchParams.get(GUIDE_ACTION_PARAM) !== actionId) {
            handled.current = null;
            return;
        }
        const request = searchParams.toString();
        if (handled.current === request) return;
        /**
         * Area and tab must be **present** and match. The address is always built by
         * the assistant from `feature.route`, which carries both; an address without
         * them does not come from the guide. Stricter than `ProcessPanel` (there
         * `panel` may be missing because the area can also be the default area) —
         * here absence is a reason to do nothing, which is why `panel` is required in
         * the type and the comparison is unconditional.
         */
        if (searchParams.get('panel') !== panel) return;
        if (tab !== undefined && searchParams.get('tab') !== tab) return;

        const guideAction = findFeatureByGuideAction(actionId)?.guideAction;
        /**
         * No entry, no prefill: without `guideAction.fields` the hook would not know
         * which fields are allowed — and would guess. The registry guards
         * (`featureRegistry.test.ts`) and `guideActionPanels.test.ts` keep the ids of
         * the panels and the registry in sync.
         */
        if (guideAction === undefined) return;
        if (!ready) return;

        const values = readGuideActionValues(searchParams, guideAction.fields);

        // Before opening: the opener sets state, which triggers a render pass, and
        // the effect must not then see the same request a second time.
        handled.current = request;
        setSearchParams(
            (previous) => {
                const next = new URLSearchParams(previous);
                next.delete(GUIDE_ACTION_PARAM);
                guideAction.fields.forEach((field) => next.delete(field));
                return next;
            },
            { replace: true },
        );
        openerRef.current(values);
    }, [actionId, panel, tab, ready, searchParams, setSearchParams]);
}
